import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobileCatOpen, setIsMobileCatOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsMobileCatOpen(false);
    setIsDropdownOpen(false);
  }, [location]);

  const categoryImages: Record<string, string> = {
    'Shoes': 'https://readdy.ai/api/search-image?query=Single%20pair%20of%20pristine%20white%20leather%20sneakers%20on%20warm%20cream%20surface%2C%20dramatic%20side%20lighting%20with%20soft%20shadows%2C%20minimalist%20product%20photography%2C%20clean%20and%20modern%20aesthetic%2C%20subtle%20reflection%2C%20high%20end%20catalog%20style%2C%20warm%20neutral%20atmosphere&width=120&height=120&seq=nav-cat-shoes&orientation=squarish',
    'Hoodies / Sweaters': 'https://readdy.ai/api/search-image?query=Cozy%20oversized%20cream%20knit%20sweater%20neatly%20folded%20on%20warm%20beige%20surface%2C%20soft%20diffused%20window%20light%2C%20minimalist%20textile%20photography%2C%20visible%20fabric%20texture%20detail%2C%20calm%20and%20comforting%20aesthetic%2C%20warm%20tonal%20palette&width=120&height=120&seq=nav-cat-hoodies&orientation=squarish',
    'T-Shirts': 'https://readdy.ai/api/search-image?query=Crisp%20white%20cotton%20t-shirt%20folded%20cleanly%20on%20warm%20neutral%20surface%2C%20soft%20natural%20daylight%2C%20minimalist%20fashion%20product%20photography%2C%20smooth%20fabric%20texture%20visible%2C%20simple%20and%20fresh%20aesthetic&width=120&height=120&seq=nav-cat-tshirts&orientation=squarish',
    'Jackets': 'https://readdy.ai/api/search-image?query=Stylish%20olive%20green%20bomber%20jacket%20hanging%20on%20wooden%20hanger%2C%20warm%20cream%20wall%20background%2C%20soft%20directional%20lighting%2C%20minimalist%20outerwear%20photography%2C%20subtle%20fabric%20sheen%2C%20modern%20urban%20style&width=120&height=120&seq=nav-cat-jackets&orientation=squarish',
    'Pants / Shorts': 'https://readdy.ai/api/search-image?query=Well%20fitted%20beige%20chino%20pants%20folded%20neatly%20on%20warm%20surface%2C%20soft%20natural%20lighting%20from%20side%2C%20minimalist%20fashion%20photography%2C%20clean%20and%20structured%20look%2C%20warm%20earth%20tone%20palette&width=120&height=120&seq=nav-cat-pants&orientation=squarish',
    'Headwear': 'https://readdy.ai/api/search-image?query=Classic%20baseball%20cap%20in%20warm%20sand%20color%20on%20cream%20background%2C%20soft%20overhead%20lighting%2C%20minimalist%20accessory%20photography%2C%20subtle%20shadow%20detail%2C%20casual%20streetwear%20aesthetic&width=120&height=120&seq=nav-cat-headwear&orientation=squarish',
    'Sets': 'https://readdy.ai/api/search-image?query=Coordinated%20matching%20outfit%20set%20with%20top%20and%20bottom%20in%20harmonious%20earth%20tones%20on%20warm%20cream%20surface%2C%20soft%20diffused%20light%2C%20minimalist%20fashion%20still%20life%2C%20elegant%20and%20cohesive%20styling&width=120&height=120&seq=nav-cat-sets&orientation=squarish',
    'Underwear / Underpants': 'https://readdy.ai/api/search-image?query=Soft%20cotton%20undergarment%20basics%20in%20warm%20neutral%20tones%20neatly%20arranged%20on%20cream%20backdrop%2C%20gentle%20diffused%20lighting%2C%20minimalist%20intimate%20apparel%20photography%2C%20clean%20and%20discreet%20presentation&width=120&height=120&seq=nav-cat-underwear&orientation=squarish',
    'Jersey': 'https://readdy.ai/api/search-image?query=Modern%20athletic%20jersey%20with%20subtle%20geometric%20pattern%20on%20warm%20cream%20background%2C%20soft%20studio%20lighting%2C%20minimalist%20sportswear%20photography%2C%20performance%20fabric%20texture%20detail%2C%20clean%20sporty%20aesthetic&width=120&height=120&seq=nav-cat-jersey&orientation=squarish',
    'Accessories': 'https://readdy.ai/api/search-image?query=Elegant%20minimalist%20wristwatch%20with%20leather%20strap%20on%20warm%20cream%20surface%2C%20soft%20directional%20light%20creating%20gentle%20shadows%2C%20refined%20product%20photography%2C%20sophisticated%20and%20timeless%20aesthetic&width=120&height=120&seq=nav-cat-accessories&orientation=squarish',
    'Bags': 'https://readdy.ai/api/search-image?query=Structured%20tan%20leather%20tote%20bag%20on%20warm%20cream%20surface%2C%20soft%20natural%20window%20light%2C%20minimalist%20accessories%20photography%2C%20clean%20lines%20and%20premium%20texture%2C%20modern%20sophisticated%20style&width=120&height=120&seq=nav-cat-bags&orientation=squarish',
    'Belts': 'https://readdy.ai/api/search-image?query=Handcrafted%20cognac%20leather%20belt%20coiled%20elegantly%20on%20warm%20cream%20surface%2C%20soft%20side%20lighting%2C%20minimalist%20product%20photography%2C%20visible%20grain%20texture%2C%20refined%20classic%20accessory&width=120&height=120&seq=nav-cat-belts&orientation=squarish',
    'Perfumes': 'https://readdy.ai/api/search-image?query=Elegant%20glass%20perfume%20bottle%20with%20warm%20amber%20liquid%20on%20cream%20backdrop%2C%20soft%20diffused%20studio%20lighting%20with%20gentle%20reflections%2C%20minimalist%20fragrance%20product%20photography%2C%20sophisticated%20and%20luxurious%20aesthetic&width=120&height=120&seq=nav-cat-perfumes&orientation=squarish',
    'Other Stuff': 'https://readdy.ai/api/search-image?query=Eclectic%20mix%20of%20lifestyle%20items%20including%20sunglasses%20and%20small%20gadgets%20on%20warm%20cream%20surface%2C%20soft%20natural%20lighting%2C%20minimalist%20product%20still%20life%2C%20contemporary%20and%20casual%20aesthetic&width=120&height=120&seq=nav-cat-other&orientation=squarish',
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/70'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="flex items-center justify-between h-16 md:h-18">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 group flex-shrink-0">
            <div className="w-8 h-8 md:w-9 md:h-9 rounded-lg bg-primary-500 flex items-center justify-center transition-transform duration-300 group-hover:scale-105">
              <span className="text-background-50 font-heading font-bold text-sm">K</span>
            </div>
            <span className="font-heading text-base md:text-lg font-semibold text-foreground-900 whitespace-nowrap">
              Kakobuy <span className="text-primary-500">Polska</span>
            </span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-1">
            <Link
              to="/"
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 cursor-pointer whitespace-nowrap ${
                location.pathname === '/'
                  ? 'text-primary-600 bg-primary-50'
                  : 'text-foreground-700 hover:text-primary-600 hover:bg-primary-50'
              }`}
            >
              Strona Główna
            </Link>

            {/* Category Dropdown */}
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium text-foreground-700 hover:text-primary-600 hover:bg-primary-50 transition-all duration-200 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-grid-line text-base"></i>
                <span>Kategorie</span>
                <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {isDropdownOpen && (
                <div className="absolute top-full left-0 mt-1 w-72 bg-background-50 rounded-xl border border-background-200/70 shadow-lg overflow-hidden animate-[fadeIn_0.2s_ease-out]">
                  <div className="max-h-96 overflow-y-auto py-2">
                    {categories.map((cat) => (
                      <a
                        key={cat.name}
                        href={cat.url}
                        target="_blank"
                        rel="noopener nofollow"
                        className="flex items-center gap-3 px-4 py-3 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-600 transition-colors duration-150 cursor-pointer"
                        onClick={() => setIsDropdownOpen(false)}
                      >
                        <div className="w-10 h-10 rounded-lg overflow-hidden bg-background-200 flex-shrink-0">
                          <img
                            src={categoryImages[cat.name]}
                            alt={cat.name}
                            className="w-full h-full object-cover object-top"
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <span className="block text-sm font-medium whitespace-nowrap">{cat.name}</span>
                          <span className="block text-xxs text-foreground-400">Kliknij, aby zobaczyć produkty</span>
                        </div>
                        <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                          <i className="ri-external-link-line text-xs text-foreground-400"></i>
                        </div>
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <Link
              to="/artykul/kakobuy-spreadsheet-poradnik"
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 cursor-pointer whitespace-nowrap ${
                location.pathname.includes('/artykul/')
                  ? 'text-primary-600 bg-primary-50'
                  : 'text-foreground-700 hover:text-primary-600 hover:bg-primary-50'
              }`}
            >
              Poradnik Zakupowy
            </Link>

            <a href="#jak-dziala" className="px-4 py-2 rounded-lg text-sm font-medium text-foreground-700 hover:text-primary-600 hover:bg-primary-50 transition-all duration-200 cursor-pointer whitespace-nowrap">
              Jak działa
            </a>
            <a href="#faq" className="px-4 py-2 rounded-lg text-sm font-medium text-foreground-700 hover:text-primary-600 hover:bg-primary-50 transition-all duration-200 cursor-pointer whitespace-nowrap">
              FAQ
            </a>
          </div>

          {/* CTA Button */}
          <div className="flex items-center gap-3">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="hidden sm:inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-all duration-200 cursor-pointer whitespace-nowrap"
            >
              Buy It Now
              <i className="ri-arrow-right-line text-sm"></i>
            </a>

            {/* Mobile Menu Toggle */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden w-9 h-9 flex items-center justify-center rounded-lg text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer"
            >
              <i className={`text-xl ${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <div
        className={`md:hidden transition-all duration-300 ${
          isMobileMenuOpen ? 'max-h-[calc(100vh-4rem)] opacity-100 overflow-y-auto' : 'max-h-0 opacity-0 overflow-hidden'
        }`}
      >
        <div className="bg-background-50 border-t border-background-200/70 px-4 py-4 space-y-1">
          {/* Mobile Nav Links */}
          <Link
            to="/"
            className="flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-medium text-foreground-700 hover:bg-primary-50 hover:text-primary-600 transition-colors cursor-pointer"
            onClick={() => setIsMobileMenuOpen(false)}
          >
            <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-primary-100">
              <i className="ri-home-line text-primary-500 text-sm"></i>
            </div>
            Strona Główna
          </Link>

          <Link
            to="/artykul/kakobuy-spreadsheet-poradnik"
            className="flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-medium text-foreground-700 hover:bg-primary-50 hover:text-primary-600 transition-colors cursor-pointer"
            onClick={() => setIsMobileMenuOpen(false)}
          >
            <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-secondary-100">
              <i className="ri-book-open-line text-secondary-500 text-sm"></i>
            </div>
            Poradnik Zakupowy
          </Link>

          {/* Mobile Category Toggle */}
          <button
            onClick={() => setIsMobileCatOpen(!isMobileCatOpen)}
            className="flex items-center justify-between w-full px-3 py-3 rounded-lg text-sm font-medium text-foreground-700 hover:bg-primary-50 hover:text-primary-600 transition-colors cursor-pointer"
          >
            <div className="flex items-center gap-3">
              <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-accent-100">
                <i className="ri-grid-line text-accent-500 text-sm"></i>
              </div>
              Kategorie
            </div>
            <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${isMobileCatOpen ? 'rotate-180' : ''}`}></i>
          </button>

          {/* Mobile Category List */}
          {isMobileCatOpen && (
            <div className="pl-4 space-y-0.5 border-l-2 border-background-200/70 ml-3 animate-[fadeIn_0.2s_ease-out]">
              {categories.map((cat) => (
                <a
                  key={cat.name}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-600 transition-colors cursor-pointer"
                >
                  <div className="w-8 h-8 rounded-lg overflow-hidden bg-background-200 flex-shrink-0">
                    <img
                      src={categoryImages[cat.name]}
                      alt={cat.name}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                  <span className="text-sm">{cat.name}</span>
                </a>
              ))}
            </div>
          )}

          <a href="#jak-dziala" className="flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-medium text-foreground-700 hover:bg-primary-50 transition-colors cursor-pointer" onClick={() => setIsMobileMenuOpen(false)}>
            <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-background-200">
              <i className="ri-play-circle-line text-foreground-500 text-sm"></i>
            </div>
            Jak działa
          </a>
          <a href="#faq" className="flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-medium text-foreground-700 hover:bg-primary-50 transition-colors cursor-pointer" onClick={() => setIsMobileMenuOpen(false)}>
            <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-background-200">
              <i className="ri-question-line text-foreground-500 text-sm"></i>
            </div>
            FAQ
          </a>

          <div className="pt-3 border-t border-background-200/70">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="flex items-center justify-center gap-2 w-full px-5 py-3.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-all duration-200 cursor-pointer whitespace-nowrap"
            >
              Buy It Now
              <i className="ri-arrow-right-line text-sm"></i>
            </a>
          </div>
        </div>
      </div>
    </nav>
  );
}